import React, { useEffect, useMemo, useRef } from 'react'
import io from 'socket.io-client'
import { Auth } from 'aws-amplify'
import { getJWT, initiateAnalytics, isScreenVU } from '../../../utils/functions'
import { CurrentUserContext } from '../../../context/Shared/CurrentUser.context'
import { CurrentPortalContext } from '../../../context/Shared/CurrentPortal.context'
import { KioskModeContext } from '../../../context/Shared/KioskMode.context'
import { pathNameMap } from '../../../utils/constants'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { NotificationContext } from '../../../context/Shared/Notification.context'
import { theme } from '../../../styles/theme.style'

const ENDPOINT = import.meta.env.VITE_BACKEND

const Socket = ({
  setSocket,
  setOnline,
  setOnlineStatus,
  setIncomingCall,
  setCallFrom,
  setIncomingAddToCall,
  goToSession,
  signOut,
  location,
  setUserBeingCalledId,
  setOutgoingCall
}) => {
  const { me } = React.useContext(CurrentUserContext)
  const { portal } = React.useContext(CurrentPortalContext)
  const { kioskMode } = React.useContext(KioskModeContext)
  const { t } = useTranslation()
  const { setSnackBarMessage } = React.useContext(NotificationContext)

  // Create refs for values used in event handlers to prevent stale closures
  const locationRef = useRef(location)
  const goToSessionRef = useRef(goToSession)
  const signOutRef = useRef(signOut)
  const setSnackBarMessageRef = useRef(setSnackBarMessage)
  const setIncomingCallRef = useRef(setIncomingCall)
  const setCallFromRef = useRef(setCallFrom)
  const setIncomingAddToCallRef = useRef(setIncomingAddToCall)
  const setUserBeingCalledIdRef = useRef(setUserBeingCalledId)
  const setOutgoingCallRef = useRef(setOutgoingCall)
  const setOnlineRef = useRef(setOnline)
  const setOnlineStatusRef = useRef(setOnlineStatus)
  const autoAcceptCallsRef = useRef(me.autoAcceptCalls)
  const kioskModeRef = useRef(kioskMode)

  // Update refs whenever values change
  useEffect(() => {
    locationRef.current = location
    goToSessionRef.current = goToSession
    signOutRef.current = signOut
    setSnackBarMessageRef.current = setSnackBarMessage
    setIncomingCallRef.current = setIncomingCall
    setCallFromRef.current = setCallFrom
    setIncomingAddToCallRef.current = setIncomingAddToCall
    setUserBeingCalledIdRef.current = setUserBeingCalledId
    setOutgoingCallRef.current = setOutgoingCall
    setOnlineRef.current = setOnline
    setOnlineStatusRef.current = setOnlineStatus
    autoAcceptCallsRef.current = me.autoAcceptCalls
    kioskModeRef.current = kioskMode
  }, [
    location,
    goToSession,
    signOut,
    setSnackBarMessage,
    setIncomingCall,
    setCallFrom,
    setIncomingAddToCall,
    setUserBeingCalledId,
    setOutgoingCall,
    setOnline,
    setOnlineStatus,
    me.autoAcceptCalls,
    kioskMode
  ])

  // Extract only the specific fields from me that are actually used for auth
  // This prevents socket reconnection when unrelated user fields change
  const userAuth = useMemo(
    () => ({
      email: me.email,
      name: me.name,
      userType: isScreenVU(me) ? 'ScreenVU' : 'Operator',
      recordingMode: me.recordingMode,
      preferredVideoCodec: me.preferredVideoCodec,
      preferredVideoResolution: me.preferredVideoResolution
    }),
    [
      me.email,
      me.name,
      me.userType,
      me.recordingMode,
      me.preferredVideoCodec,
      me.preferredVideoResolution
    ]
  )

  // Memoize analytics data
  const analyticsData = useMemo(
    () => ({
      email: me.email,
      name: me.name,
      portal: portal.name,
      role: portal.role
    }),
    [me.email, me.name, portal.name, portal.role]
  )

  useEffect(() => {
    let socket = null
    let cancelled = false

    const initializeSocket = async () => {
      const token = await getJWT()

      // If effect was cleaned up while awaiting token, don't create socket
      if (cancelled) return

      socket = io.connect(ENDPOINT, {
        transports: ['websocket'],
        auth: {
          userId: userAuth.email,
          userType: userAuth.userType,
          token,
          portal: portal.name,
          name: userAuth.name,
          recordingMode: userAuth.recordingMode,
          preferredVideoCodec: userAuth.preferredVideoCodec,
          preferredVideoResolution: userAuth.preferredVideoResolution
        },
        withCredentials: true
      })

      // Double-check in case cleanup ran between connect and here
      if (cancelled) {
        socket.off('connect')
        socket.io.close()
        socket = null
        return
      }

      setSocket(socket)

      socket.on('connect', () => {
        setOnlineStatusRef.current(true)
      })

      socket.on('disconnect', () => {
        setOnlineStatusRef.current(false)
      })

      socket.on('connect_error', (err) => {
        setOnlineRef.current({})
        setOnlineStatusRef.current(false)
        if (err.message === 'Authentication error') {
          window.location.reload(false)
        }
        if (err.message === 'force_disconnect') {
          socket.io.skipReconnect = true
        }
      })

      socket.on('user list', (data) => {
        setOnlineRef.current(data)
      })

      socket.on('initiate call', (data) => {
        if (pathNameMap[locationRef.current.pathname] !== theme.SESSION) {
          // Auto-accept calls in kiosk mode or if user has autoAcceptCalls enabled
          if (autoAcceptCallsRef.current || kioskModeRef.current) {
            const id = uuidv4()
            const to = data.from.socketId
            socket.emit('accept call', {
              id,
              to
            })
            goToSessionRef.current(id)
          } else {
            setIncomingCallRef.current(true)
            setCallFromRef.current(data)
          }
        }
      })

      socket.on('accept call', (data) => {
        setSnackBarMessageRef.current(t('Call accepted. Connecting you...'))
        setOutgoingCallRef.current(false)
        setTimeout(() => {
          goToSessionRef.current(data.id)
        }, 500)
        setUserBeingCalledIdRef.current(null)
      })

      socket.on('add to call', (data) => {
        if (pathNameMap[locationRef.current.pathname] !== theme.SESSION) {
          // Auto-join calls in kiosk mode or if user has autoAcceptCalls enabled
          if (autoAcceptCallsRef.current || kioskModeRef.current) {
            goToSessionRef.current(data.id)
          } else {
            setIncomingAddToCallRef.current(true)
            setCallFromRef.current(data)
          }
        }
      })

      socket.on('reject call', () => {
        setSnackBarMessageRef.current(t('Call rejected'), 'error')
        setOutgoingCallRef.current(false)
        setUserBeingCalledIdRef.current(null)
      })

      socket.on('cancel call', () => {
        setIncomingCallRef.current(false)
        setIncomingAddToCallRef.current(false)
      })

      socket.on('logout', () => {
        console.log('Received logout event from server (ignored)')
      })

      socket.on('force_disconnect', (data) => {
        setSnackBarMessageRef.current(
          data?.reason || 'Your connection was terminated by an administrator',
          'error'
        )
        setOnlineRef.current({})
        setOnlineStatusRef.current(false)
        socket.io.skipReconnect = true
        socket.io.close()
        // Reload after a short delay as fallback in case Auth.signOut() hangs
        const reloadTimeout = setTimeout(() => window.location.reload(), 2000)
        Auth.signOut()
          .catch(() => {})
          .finally(() => {
            clearTimeout(reloadTimeout)
            window.location.reload()
          })
      })

      initiateAnalytics(me.id, analyticsData)
    }

    initializeSocket()

    return () => {
      cancelled = true
      if (socket) {
        socket.off('connect')
        socket.off('disconnect')
        socket.off('connect_error')
        socket.off('user list')
        socket.off('initiate call')
        socket.off('accept call')
        socket.off('add to call')
        socket.off('reject call')
        socket.off('cancel call')
        socket.off('logout')
        socket.off('force_disconnect')
        socket.io.close()
      }
    }
  }, [portal.name, userAuth, analyticsData, me.id, t, setSocket])

  return null
}

Socket.propTypes = {
  setSocket: PropTypes.func,
  setOnline: PropTypes.func,
  setOnlineStatus: PropTypes.func,
  setIncomingCall: PropTypes.func,
  setCallFrom: PropTypes.func,
  setIncomingAddToCall: PropTypes.func,
  goToSession: PropTypes.func,
  signOut: PropTypes.func,
  location: PropTypes.object,
  setUserBeingCalledId: PropTypes.func
}

export default React.memo(Socket)
